import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";
import { useTheme } from "../contexts/ThemeContext";
import SimpleAnalyticsDashboard from "../components/SimpleAnalyticsDashboard";
import SimpleMovieCalendar from "../components/SimpleMovieCalendar";
import SimpleRecommendations from "../components/SimpleRecommendations";

function Profile() {
  const { isDark } = useTheme();
  const [user, setUser] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get("/user/profile");
        setUser(res.data);
      } catch (err) {
        // Profile endpoint not available, using mock data
        // Provide mock data when profile endpoint is not available
        setUser({
          name: "Demo User",
          email: "demo@example.com",
          favorites: [],
          watchlist: [],
          reviews: []
        });
      }
    };

    const fetchRecommendations = async () => {
      try {
        const res = await api.get("/user/recommendations");
        setRecommendations(res.data.recommendations);
      } catch (err) {
        // Recommendations endpoint not available, using empty array
        setRecommendations([]);
      }
    };

    fetchProfile();
    fetchRecommendations();
  }, []);

  const handleDeleteReview = async (reviewId) => {
    try {
      const res = await api.delete(`/reviews/${reviewId}`);
      setUser((prev) => ({
        ...prev,
        reviews: res.data.reviews,
      }));
    } catch (err) {
      // Delete review endpoint not available
      // Remove from local state if API call fails
      setUser((prev) => ({
        ...prev,
        reviews: prev.reviews.filter(review => review._id !== reviewId)
      }));
    }
  };

  if (error) return (
    <div className="min-h-screen theme-bg-primary flex items-center justify-center">
      <div className="text-center">
        <p className="text-red-500 text-xl">{error}</p>
      </div>
    </div>
  );

  if (!user) return (
    <div className="min-h-screen theme-bg-primary flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
        <p className="theme-text-secondary">Loading profile...</p>
      </div>
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '👤' },
    { id: 'analytics', label: 'Analytics', icon: '📊' },
    { id: 'calendar', label: 'Calendar', icon: '📅' },
    { id: 'recommendations', label: 'Recommendations', icon: '🎯' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'analytics':
        return <SimpleAnalyticsDashboard />;
      case 'calendar':
        return <SimpleMovieCalendar />;
      case 'recommendations':
        return <SimpleRecommendations />;
      default:
        return (
          <div className="space-y-8">
            {/* User Info */}
            <div className="theme-bg-secondary rounded-xl p-8 theme-shadow-lg theme-border border">
              <div className="flex items-center space-x-6">
                <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-3xl font-bold">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-3xl font-bold theme-text-primary mb-2">{user.name}</h2>
                  <p className="theme-text-secondary text-lg">📧 {user.email}</p>
                  <div className="flex items-center space-x-6 mt-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold theme-text-primary">{user.favorites.length}</div>
                      <div className="text-sm theme-text-tertiary">Favorites</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold theme-text-primary">{user.watchlist.length}</div>
                      <div className="text-sm theme-text-tertiary">Watchlist</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold theme-text-primary">{user.reviews.length}</div>
                      <div className="text-sm theme-text-tertiary">Reviews</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Favorites */}
            <Section title="❤️ Favorite Movies" items={user.favorites} />

            {/* Watchlist */}
            <Section title="📺 Watchlist" items={user.watchlist} />

            {/* Reviews */}
            <div className="theme-bg-secondary rounded-xl p-6 theme-shadow-lg theme-border border">
              <h3 className="text-2xl font-bold theme-text-primary mb-6">📝 Your Reviews</h3>
              {user.reviews.length === 0 ? (
                <p className="theme-text-tertiary italic">No reviews yet.</p>
              ) : (
                <div className="space-y-4">
                  {user.reviews.map((review, idx) => (
                    <div
                      key={idx}
                      className="theme-bg-tertiary p-4 rounded-lg theme-border border relative"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold theme-text-primary">Movie ID: {review.movieId}</p>
                          <p className="theme-text-secondary">⭐ Rating: {review.rating}/10</p>
                          {review.comment && (
                            <p className="theme-text-secondary mt-2">{review.comment}</p>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteReview(review._id)}
                          className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen theme-bg-primary">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-700 text-white py-8 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold mb-3 sm:mb-4">
            Your Profile
          </h1>
          <p className="text-sm sm:text-base md:text-xl lg:text-2xl text-white/90 px-4">
            Manage your movie collections and discover new favorites
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 -mt-6 sm:-mt-8 relative z-10">
        <div className="theme-bg-secondary rounded-lg sm:rounded-xl theme-shadow-xl theme-border border overflow-hidden">
          <div className="flex flex-wrap border-b theme-border">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-3 sm:px-4 lg:px-6 py-3 sm:py-4 text-center font-semibold transition-all duration-200 text-sm sm:text-base ${activeTab === tab.id
                  ? 'bg-purple-600 text-white'
                  : 'theme-text-primary hover:theme-bg-tertiary'
                  }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-4 sm:p-6 lg:p-8">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
}

// Reusable section with optional actions
const Section = ({ title, items, showActions = false }) => {
  const handleAdd = async (type, movie) => {
    try {
      await api.post(`/user/${type}`, {
        movieId: movie.movieId,
        title: movie.title,
        posterPath: movie.posterPath,
      });
      alert(`Added to ${type}`);
    } catch (err) {
      // ${type} endpoint not available
      alert(`Feature not available - ${type} endpoint not implemented`);
    }
  };

  return (
    <div className="theme-bg-secondary rounded-xl p-6 theme-shadow-lg theme-border border">
      <h3 className="text-2xl font-bold theme-text-primary mb-6">{title}</h3>
      {items.length === 0 ? (
        <p className="theme-text-tertiary italic">No items to display.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {items.map((item, idx) => (
            <div
              key={idx}
              className="theme-bg-tertiary rounded-lg overflow-hidden theme-shadow hover:theme-shadow-lg transition duration-200 group"
            >
              <Link to={`/movie/${item.movieId}`}>
                {item.posterPath ? (
                  <img
                    src={`https://image.tmdb.org/t/p/w300${item.posterPath}`}
                    alt={item.title}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-64 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center">
                    <span className="text-gray-500 dark:text-gray-400">No image</span>
                  </div>
                )}
              </Link>
              <div className="p-3 text-center text-sm font-medium theme-text-primary line-clamp-2">
                {item.title}
              </div>

              {showActions && (
                <div className="flex justify-around mb-3 px-2 space-x-2">
                  <button
                    onClick={() => handleAdd("favorites", item)}
                    className="bg-pink-500 text-white text-xs px-2 py-1 rounded hover:bg-pink-600 transition-colors flex-1"
                  >
                    ❤️ Favorite
                  </button>
                  <button
                    onClick={() => handleAdd("watchlist", item)}
                    className="bg-blue-500 text-white text-xs px-2 py-1 rounded hover:bg-blue-600 transition-colors flex-1"
                  >
                    📺 Watchlist
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Profile;